// View-template helpers for the Port Calendar. Templates store terminal
// externalRef codes (stable across renames); the calendar selectedPorts
// state holds full port names. Convert at the load/save boundary.
//
// Codes round-trip through the same `state.portRefs` (portName ->
// externalRef) map the URL state machine already uses, so a code that
// doesn't currently resolve to a name is dropped on load — likely the
// terminal was removed from the catalogue.

import { createSignal } from "solid-js";
import {
  deleteViewTemplate,
  getCurrentView,
  getViewTemplates,
  postViewTemplate,
  putCurrentView,
  putViewTemplate,
  type ViewTemplate,
} from "../../shared/api/endpoints";
import { createCoalescedSaver } from "../../shared/util/coalesce";

export type { ViewTemplate } from "../../shared/api/endpoints";

// Module-scoped signal so the PortSelector popup, Settings list, and
// any future surface (e.g. status bar pill) share the same up-to-date
// template catalogue without each one re-fetching.
const [templates, setTemplates] = createSignal<ViewTemplate[]>([]);

export const viewTemplates = templates;

export async function loadViewTemplates(): Promise<ViewTemplate[]> {
  try {
    const list = await getViewTemplates();
    setTemplates(list);
    return list;
  } catch (err) {
    console.warn("[view-templates] load failed:", err);
    return templates();
  }
}

export async function saveTemplate(
  args:
    | { mode: "create"; name: string; ports: string[] }
    | { mode: "overwrite"; id: number; name: string; ports: string[] },
): Promise<ViewTemplate | { error: "name-taken" | "not-found" }> {
  const result = args.mode === "create"
    ? await postViewTemplate(args.name, args.ports)
    : await putViewTemplate(args.id, args.name, args.ports);
  if ("error" in result) return result;
  // Optimistic local update so the dropdown reflects the new state
  // immediately; loadViewTemplates() can confirm later if needed.
  const next = args.mode === "create"
    ? [...templates(), result].sort((a, b) => a.name.localeCompare(b.name))
    : templates().map((t) => (t.id === result.id ? result : t));
  setTemplates(next);
  return result;
}

export async function removeTemplate(id: number): Promise<void> {
  await deleteViewTemplate(id);
  setTemplates(templates().filter((t) => t.id !== id));
}

// Translate template port codes -> portNames using the catalogue. Drops
// codes that don't resolve (rare; only if a terminal was removed from
// the catalogue between save and load).
export function portCodesToNames(
  codes: ReadonlyArray<string>,
  portRefs: Record<string, string>,
): string[] {
  const codeToName = new Map<string, string>();
  for (const [name, ref] of Object.entries(portRefs)) codeToName.set(ref, name);
  const out: string[] = [];
  for (const code of codes) {
    const name = codeToName.get(code);
    if (name) out.push(name);
  }
  return out;
}

export function portNamesToCodes(
  names: ReadonlyArray<string>,
  portRefs: Record<string, string>,
): string[] {
  return names.map((n) => portRefs[n] ?? n);
}

// Current view — per-user auto-saved selection. Loaded once at app start;
// every selectedPorts change writes a fresh copy.
//
// Returns the "failed" sentinel (instead of [] like a clean "no saved
// view") when the fetch itself errors out. Conflating fetch failure
// with a genuinely-empty saved view was the original "ports gone on
// deploy" symptom: a transient 502 during a Caddy reshuffle made the
// load return [], the save effect ran on the user's next interaction,
// and PUT '[]' overwrote the real saved state. Callers should keep
// the save path gated until they actually have a successful read.
export type LoadCurrentViewResult = string[] | "failed";

export async function loadCurrentView(): Promise<LoadCurrentViewResult> {
  try {
    const view = await getCurrentView();
    return view.ports;
  } catch (err) {
    console.warn("[current-view] load failed:", err);
    return "failed";
  }
}

// Serialised saves so a rapid flurry of selectedPorts writes (drag-
// reorder loop, multi-add, etc.) doesn't race. The last queued payload
// wins; intermediate states are coalesced. See shared/util/coalesce.ts
// for the underlying mechanism + its tests.
const currentViewSaver = createCoalescedSaver<string[]>({
  save: (codes) => putCurrentView(codes),
  onError: (err) => console.warn("[current-view] save failed:", err),
});

export function saveCurrentView(codes: ReadonlyArray<string>): void {
  currentViewSaver.enqueue([...codes]);
}
